/*
 * This file is part of the Norges Musikkhøyskole Nettsted 2020 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import IconQuestionMark from 'gfx/icons/question-mark.svg';

import styles from './Search.module.scss';

/**
 * This is the Search component.
 *
 * @author Anders Stalheim Øfsdahl <anders@apt.no>
 *
 * @return {JSX}
 */
function Search({
  open,
  inline,
  standalone,
  query,
  merge,
  action,
  headingLevel,
  hits,
  suggestions,
  labels,
}) {
  // console.log('Search', open, inline, standalone, query, merge, action, hits, suggestions);

  const formRef = useRef(null);

  const setRef = (ref) => {
    if (!ref) {
      return null;
    }

    formRef.current = ref;

    // Find input field and set focus
    const input = formRef.current.querySelector('input');
    if (input) {
      input.focus();
    }
  };

  const handleSubmit = (e) => {
    if (!merge) {
      return true;
    }

    e.preventDefault();

    const searchQuery = formRef.current.children.search.value;
    const actionUrl = action.replace('[PARAM]', searchQuery);
    location.href = actionUrl;

    // const formData = new FormData(formRef.current);
    // formData.forEach((value, key) => {
    //   if (key == 'search') {
    //     searchQuery = value;
    //   }
    // });
  };

  const render = () => {
    return (
      <div
        className={classNames(styles.main, {
          [styles.inline]: inline,
          [styles.open]: open,
          [styles.standalone]: standalone,
        })}
      >
        <div className={classNames({ ['content-width']: !standalone })}>
          {headingLevel == 1 && <h1>{labels.header}</h1>}
          {headingLevel == 2 && <h2>{labels.header}</h2>}
          <div className={styles.search}>
            <IconQuestionMark />
            <form
              method="GET"
              action={action}
              onSubmit={handleSubmit}
              ref={setRef}
            >
              <input
                type="text"
                name="search"
                placeholder={labels.placeholder}
                defaultValue={query ? query : ''}
              />
            </form>
          </div>
          {hits && hits.length > 0 && (
            <div className={styles.hits}>
              {hits.map((hit) => (
                <>
                  {hit.url && (
                    <a href={hit.url} className="chip" key={hit.label}>
                      {hit.label} ({hit.count})
                    </a>
                  )}
                  {!hit.url && (
                    <span className="chip" key={hit.label}>
                      {hit.label} ({hit.count})
                    </span>
                  )}
                </>
              ))}
            </div>
          )}
          {suggestions && suggestions.length > 0 && (
            <>
              <h3>{labels.headerSuggestions}</h3>
              <ul className={styles.suggestions}>
                {suggestions.map((suggestion, index) => (
                  <li key={`${suggestion.url}_${index}`}>
                    <a href={suggestion.url}>{suggestion.text}</a>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    );
  };

  if (!action) {
    return null;
  }

  if (inline) {
    return <div className="header__underneath">{render()}</div>;
  } else {
    return render();
  }
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Search.propTypes = {
  open: PropTypes.bool,
  query: PropTypes.string,
  merge: PropTypes.bool,
  inline: PropTypes.bool,
  standalone: PropTypes.bool,
  action: PropTypes.string,
  headingLevel: PropTypes.oneOf([1, 2]),
  hits: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      count: PropTypes.number,
    })
  ),
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
      text: PropTypes.string,
    })
  ),
  labels: PropTypes.shape({
    header: PropTypes.string,
    placeholder: PropTypes.string,
    headerSuggestions: PropTypes.string,
  }),
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Search.defaultProps = {
  open: false,
  query: null,
  merge: false,
  inline: true,
  standalone: false,
  action: null,
  headingLevel: 1,
  hits: [],
  suggestions: [],
  labels: {
    header: 'Hva leter du etter?',
    placeholder: 'Søk etter studenter, timeplaner eller annet',
    headerSuggestions: 'Søkes mest på',
  },
};

export default Search;
